import { useCallback } from "react";
import {
  SelectableNodeTypes,
  interfacesAppAtom,
  chatbotsAppAtom,
  tablesAppAtom,
  uuid,
  selectedAssetIdAtom,
  GetNodeDataByTypeFnParams,
  isAiPageAtom,
} from "@zapier/canvas-state";
import { useAtomValue } from "jotai";

function getNodeDataValues(
  selectedNodeType: SelectableNodeTypes,
  selectedAssetId: string | null,
) {
  switch (selectedNodeType) {
    case "zap":
      return {
        zapId: selectedAssetId,
        isExpanded: false,
      };
    default:
      return {};
  }
}

function useGetNodeDataByTypeFn() {
  const tablesApp = useAtomValue(tablesAppAtom);
  const interfacesApp = useAtomValue(interfacesAppAtom);
  const chatbotsApp = useAtomValue(chatbotsAppAtom);
  const selectedAssetId = useAtomValue(selectedAssetIdAtom);
  const isAiPage = useAtomValue(isAiPageAtom);

  return useCallback(
    ({
      type,
      position,
      width, // Don't set width or height or else measured width and height won't update unless you use NodeResizer
      height, // Don't set width or height or else measured width and height won't update unless you use NodeResizer
    }: GetNodeDataByTypeFnParams) => {
      //if ai page don't do a uuid do a type + 4 digit integer, this is how we determine if the node is a draft
      const targetId = isAiPage
        ? `draft-${type}-${Math.floor(Math.random() * 10000)}`
        : uuid();

      /** Get the new text node right under user's mouse click area */
      const offSet = type === "text" ? -15 : -50;

      /** Don't set width or height or else measured width and height won't update unless you use NodeResizer */
      const baseNode = {
        id: targetId,
        position: {
          x: position.x + offSet,
          y: position.y + offSet,
        },
        data: {
          attributionSourceType: "MANUAL",
          label: "",
          ...getNodeDataValues(type, selectedAssetId),
        },
      };

      switch (type) {
        case "group":
          return {
            ...baseNode,
            width, // need this or else group nodes won't draw correctly
            height, // need this or else group nodes won't draw correctly
            type: "group",
            connectable:
              false /** Need this or else we might end up with edges that connect to this and auto layout will fail */,
          };

        case "default":
          return {
            ...baseNode,
            selected: true,
            type: "default",
          };

        case "text":
          return {
            ...baseNode,
            type: "text",
            data: {
              attributionSourceType: "MANUAL",
            },
            selected: true,
            connectable:
              false /** Need this or else we might end up with edges that connect to this and auto layout will fail */,
          };

        case "path":
          return {
            ...baseNode,
            type: "path",
          };

        case "zap":
          return {
            ...baseNode,
            ...(isAiPage && {
              id: `_ZAP_${Math.floor(Math.random() * 10000)}`,
            }),
            selected: true,
            type: "zap",
          };

        case "chatbots":
          if (chatbotsApp?.current_implementation_id) {
            return {
              ...baseNode,
              selected: true,
              type: "default",
              data: {
                ...baseNode.data,
                ...(isAiPage && {
                  id: `_CHATBOT_${Math.floor(Math.random() * 10000)}`,
                }),
                app: "Chatbots by Zapier",
                services: [
                  {
                    currentImplementation: {
                      id: chatbotsApp.current_implementation_id,
                    },
                  },
                ],
              },
            };
          }

          return null;

        case "table":
          if (tablesApp?.current_implementation_id) {
            return {
              ...baseNode,
              selected: true,
              type: "default",
              data: {
                ...baseNode.data,
                ...(isAiPage && {
                  tableId: `_TABLE_${Math.floor(Math.random() * 10000)}`,
                }),
                app: "Tables by Zapier",
                services: [
                  {
                    currentImplementation: {
                      id: tablesApp.current_implementation_id,
                    },
                  },
                ],
              },
            };
          }

          return null;

        case "interface":
          if (interfacesApp?.current_implementation_id) {
            return {
              ...baseNode,
              selected: true,
              type: "default",
              data: {
                ...baseNode.data,
                ...(isAiPage && {
                  projectId: `_INTERFACE_${Math.floor(Math.random() * 10000)}`,
                }),
                app: "Interfaces by Zapier",
                services: [
                  {
                    currentImplementation: {
                      id: interfacesApp.current_implementation_id,
                    },
                  },
                ],
              },
            };
          }

          return null;

        default:
          return null;
      }
    },
    [tablesApp, interfacesApp, chatbotsApp, selectedAssetId, isAiPage],
  );
}

export default useGetNodeDataByTypeFn;
